import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../config';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import '../styles/tailwind.css';

interface Company {
  companyId: string;
  joinedAt: string;
  role: string;
}

interface TokenPayload {
  userId: string;
  email: string;
  name: string;
  companies: Company[];
  type: string;
}

const CompanySelection: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tokenPayload, setTokenPayload] = useState<TokenPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No token provided');
      setLoading(false);
      return;
    }

    try {
      // Decode the JWT token
      const payload: TokenPayload = JSON.parse(atob(token.split('.')[1]));

      // Verify it's a company selection token
      if (payload.type !== 'company_selection') {
        setError('Invalid token type');
        setLoading(false);
        return;
      }

      // Check if user has companies
      if (!payload.companies || payload.companies.length === 0) {
        setError('No companies found for this user');
        setLoading(false);
        return;
      }

      // If user only has one company, auto-select it
      if (payload.companies.length === 1) {
        handleSelectCompany(payload.companies[0].companyId, payload.companies[0].role, token);
        return;
      }

      setTokenPayload(payload);
      setLoading(false);
    } catch (err) {
      console.error('Error decoding token:', err);
      setError('Invalid token');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSelectCompany = useCallback(async (companyId: string, role: string, tempToken?: string) => {
    setSelecting(companyId);
    setError(null);

    try {
      const token = tempToken || searchParams.get('token');

      if (!token) {
        throw new Error('No token available');
      }

      // Exchange the temporary token for a company-specific JWT
      const response = await fetch(`${config.API_BASE_URL}/auth/validate/${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tempToken: token })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to select company');
      }

      const data = await response.json();

      // Store the company-specific JWT
      localStorage.setItem('dashzap_token', data.token);

      // Navigate based on user role for that company
      if (role === 'admin') {
        navigate('/admin-dashboard', { replace: true });
      } else {
        // Regular users go to user dashboard
        navigate('/user-dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error selecting company:', err);
      setError(err instanceof Error ? err.message : 'Failed to select company');
      setSelecting(null);
    }
  }, [navigate, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading your companies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600">Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenPayload) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Select Your Company</h1>
          <p className="text-lg text-gray-600">
            Welcome back, {tokenPayload.name}! Please select which company you'd like to access.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {tokenPayload.companies.map((company, index) => (
            <Card
              key={`${company.companyId}-${index}`}
              className={`cursor-pointer transition-all hover:shadow-md hover:border-indigo-400 ${
                selecting === company.companyId ? 'border-indigo-600 bg-indigo-50' : ''
              }`}
              onClick={() => !selecting && handleSelectCompany(company.companyId, company.role)}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 break-words">
                        {company.companyId}
                      </h3>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                          company.role === 'admin'
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {company.role}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Joined:</span>{' '}
                      {new Date(company.joinedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  {selecting === company.companyId && (
                    <div className="ml-4">
                      <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
                    </div>
                  )}
                  {!selecting && (
                    <div className="ml-4 text-indigo-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <p className="text-red-600 text-center">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CompanySelection;
